import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import AddNoReplyNeeded from 'actions/conversation_item/no_reply_needed/add_no_reply_needed';
import AddReaction from 'actions/conversation_item/reaction/add_reaction';
import AIActivityItem from 'models/ai_activity/ai_activity_item';
import canViewSidekickConversation from 'actions/message_automation/can_view_sidekick_conversation';
import ChatMessage from 'models/chat_message';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ConversationItemMenuItems from './conversation_item_menu_items';
import ConversationItemType from 'models/conversation_item_type';
import ConversationMessage, { MessageChannelType, MessageStatus, ModerationStatus } from 'models/conversation_message';
import ConversationNote from 'models/conversation_note';
import CopyItemLink from 'actions/composition/copy_item_link';
import CreateInstagramPrivateReplyComposition from 'actions/composition/create_instagram_private_reply_composition';
import CreateFacebookCommentReplyComposition from 'actions/composition/create_facebook_comment_reply_composition';
import CreateInstagramPublicReplyComposition from 'actions/composition/create_instagram_public_reply_composition';
import CustomChannelMessage from 'models/custom_channel_message';
import DeleteFacebookComment from 'models/modal/delete_facebook_comment';
import EditTaskItem from 'actions/composition/edit_task_item';
import EmailMessage from 'models/email_message';
import FbMessageIncoming from 'models/fb_message_incoming';
import FollowTask from 'actions/task_follower/follow_task';
import ForwardEmail from 'actions/composition/forward_email';
import HideFacebookComment from 'actions/facebook_comments/hide_facebook_comment';
import HighlightGroupedMessages from 'actions/composition/highlight_grouped_messages';
import InteractionType, { getInteractionType } from 'models/interaction_type';
import ItemContext from 'components/customer/conversation_history/conversation_items_v2/item_context';
import ItemsContext from 'components/customer/conversation_history/conversation_items_v2/items_context';
import NavigateToSidekickConversation from 'actions/message_automation/navigate_to_sidekick_conversation';
import OpenModal from 'actions/modal/open_modal';
import OpenOrRefreshCommentPanel from 'actions/task/open_or_refresh_comment_panel';
import PhoneCall from 'models/phone_call';
import RecordingStatus from 'models/recording_status';
import RedactConversationItem from 'models/modal/redact_conversation_item';
import RemoveReaction from 'actions/conversation_item/reaction/remove_reaction';
import ReplyToEmail from 'actions/composition/reply_to_email';
import ReplyToGroupMessage from 'actions/composition/reply_to_group_message';
import ReportPoorCallQuality from 'actions/conversation_item/phone_call/report_poor_call_quality';
import ShowAddObserverToCallModal from 'actions/conversation/show_add_observer_to_call_modal';
import ShowDeleteVoiceRecordingModal from 'actions/conversation/show_delete_voice_recording_modal';
import SmsMessage from 'models/sms_message';
import Task from 'models/task';
import TaskAnalyticsLocations from 'actions/conversation_item/task/task_analytics_locations';
import TranslationLanguages from 'models/languages/translation_languages';
import UnfollowTask from 'actions/task_follower/unfollow_task';
import Voicemail from 'models/voicemail';
import VoiceAICall from 'models/voiceai_call';
import VoiceAIMessage from 'models/voiceai_message';

function mapStateToProps(
  context,
  { currentAgentId, item, isNoReplyNeededVisible, onToggleTranslatedText, isViewTranslation }
) {
  const isConversationReadEnabled = context.isFeatureEnabled('conversation:read');
  const hasConversationGeneral = context.isFeatureEnabled('conversation:general');
  const isEmailItem = item.content instanceof EmailMessage;
  const canManageVoiceRecordings = hasConversationGeneral && canManageRecordings(context, item);
  const listenToCallsEnabled = context.isFeatureEnabled('conversation:callCoach');
  const isLivePhoneCall = item.content instanceof PhoneCall && item.content.isLive();
  const isActiveOnCall = item.content instanceof PhoneCall && item.content.isActiveParticipant(currentAgentId);
  const canObserveCall = listenToCallsEnabled && isLivePhoneCall && !isActiveOnCall;
  const canSendOutboundMessages = context.isFeatureEnabled('conversation:outbound');
  const isLinkItem = item.isLink();
  const translatedLanguage = TranslationLanguages[item.translation?.contentLanguage];
  const isTask = item.content instanceof Task;
  let isFollowVisible = false;
  let isUnfollowVisible = false;
  const taskInfoProvider = context.getProvider('taskInfo');

  const customerId = context.getProvider('profile').get()?.id;
  const highlightedMessages = context.getProvider('highlightedMessages').get();
  const groupId = getMessageGroupId(item);
  const isHighlighted = highlightedMessages?.id === groupId && highlightedMessages?.customerId === customerId;

  if (isTask) {
    let taskInfo = taskInfoProvider.findBy({ id: item.id });
    if (!taskInfo || !_.some(taskInfo.getFollowers(), f => f.agentId === currentAgentId)) {
      isFollowVisible = true;
    } else {
      isUnfollowVisible = true;
    }
  }
  const canReportPoorCallQuality = hasConversationGeneral && canAgentReportPoorCallQuality(context, item);

  return {
    canDeleteFacebookComment: canDeleteFacebookComment(context, item),
    canHideFacebookComment: canHideFacebookComment(context, item),
    canManageVoiceRecordings,
    canSendOutboundMessages,
    canObserveCall,
    canRedactItem: canRedactItem(context, item),
    canReportPoorCallQuality,
    canViewSidekickConversation: canViewSidekickConversation(context, item),
    contentType: item.contentType(),
    conversationItemId: item.id,
    customerId,
    isFollowVisible,
    isForwardVisible: isEmailItem && canSendOutboundMessages,
    isGroupMessagingItem: canGroupItems(context, item),
    isHighlighted,
    isInstagramPrivateReplyVisible: canSendInstagramPrivateReply(context, item),
    isFacebookCommentReplyVisible: canSendOutboundMessages && canSendFacebookCommentReply(context, item),
    isInstagramPublicReplyVisible: canSendInstagramPublicReply(item),
    hasConversationGeneral,
    isMenuVisible: !isLinkItem,
    isNoReplyNeededVisible,
    isInbound: item.isInbound(),
    isReactionVisible: canReact(item, canSendOutboundMessages),
    isReplyVisible: isEmailItem && canSendOutboundMessages,
    isUnfollowVisible,
    isUnreactionVisible: canUnreact(item, canSendOutboundMessages),
    isViewOriginalEmailHtmlVisible: isEmailItem && isConversationReadEnabled,
    isViewTranslation,
    isTranslateIntoVisible: item.isTranslatable() && isConversationReadEnabled,
    isToggleTranslationVisible: item.isTranslated() && !!onToggleTranslatedText && isConversationReadEnabled,
    recordingUrl: canManageVoiceRecordings ? getRecordingUrl(context, item) : null,
    translatedLanguage,
  };
}

function canGroupItems({ getProvider }, item) {
  const groupId = getMessageGroupId(item);

  if (!groupId) {
    return false;
  }

  if (item.content instanceof CustomChannelMessage) {
    const customChannelProvider = getProvider('customChannels');
    const customChannel = customChannelProvider.findBy({ id: item.content.customChannelId });
    return customChannel && customChannel.messageGroupingEnabled;
  }

  return false;
}

export function getMessageGroupId(item) {
  return item.content instanceof CustomChannelMessage ? item.content.groupId : undefined;
}

function canRedactItem(context, item) {
  if (isSocialComment(item)) {
    return false;
  }

  if (!context.isFeatureEnabled('complianceManagement')) {
    return false;
  }

  const itemContent = _.get(item, 'content');
  const isRedacted = _.get(itemContent, 'isRedacted');

  return (
    itemContent &&
    !isRedacted &&
    (itemContent instanceof AIActivityItem ||
      (itemContent instanceof ChatMessage && itemContent.canRedact()) ||
      itemContent instanceof EmailMessage ||
      itemContent instanceof FbMessageIncoming ||
      itemContent instanceof SmsMessage ||
      itemContent instanceof ConversationMessage ||
      itemContent instanceof ConversationNote ||
      itemContent instanceof CustomChannelMessage ||
      itemContent instanceof VoiceAIMessage)
  );
}

function canDeleteFacebookComment(context, item) {
  if (!context.isFeatureEnabled('conversation:socialCommentModeration') || !isSocialComment(item)) {
    return false;
  }

  return item.content.meta?.moderationStatus !== ModerationStatus.DELETED;
}

function canHideFacebookComment(context, item) {
  if (!context.isFeatureEnabled('conversation:socialCommentModeration') || !isSocialComment(item)) {
    return false;
  }

  return !item.content.meta?.moderationStatus;
}

function canAgentReportPoorCallQuality(context, item) {
  if (item.content instanceof PhoneCall) {
    const currentAgent = context.getProvider('currentAgent').get();
    const hasCallId = !!item.content.callId;
    const isAgentParticipant = currentAgent && !!item.content.findParticipantById(currentAgent.id);
    return hasCallId && isAgentParticipant;
  }

  return false;
}

function canManageRecordings(context, item) {
  if (
    !context.isFeatureEnabled('conversation:callRecordingListen') ||
    !context.isFeatureEnabled('manageVoiceRecordings')
  ) {
    return false;
  }

  if (item.content instanceof PhoneCall && !item.content.isAbandonedCall()) {
    return item.content.recordingStatus === RecordingStatus.AVAILABLE;
  }

  if (item.content instanceof Voicemail) {
    return item.content.recordingStatus === RecordingStatus.AVAILABLE;
  }

  if (item.content instanceof VoiceAICall && item.content.isCallRecorded()) {
    return item.content.getVoiceAIRecordingStatus() === RecordingStatus.AVAILABLE;
  }

  return false;
}

function getRecordingUrl(context, item) {
  let orgId = context.getProvider('auth').get().getOrgId();
  let baseUrl = `${window.location.origin}/api/v1/orgs/${orgId}/voice`;

  if (item.content instanceof PhoneCall) {
    return `${baseUrl}/call-recordings/${item.content.recordingId}`;
  }

  if (item.content instanceof VoiceAICall && item.content.isCallRecorded()) {
    const recordingId = item.content.getVoiceAIRecordingId();
    return `${baseUrl}/voice-ai-recordings/${recordingId}`;
  }

  return `${baseUrl}/voicemail-recordings/${item.content.recordingId}`;
}

function canReact(item, canSendOutboundMessages) {
  if (!canSendOutboundMessages) {
    return false;
  }

  const supportedChannels = [MessageChannelType.INSTAGRAM_DIRECT];
  // The reaction menu item should be present for Customer items of Conversation Item
  // type with a channel of Instagram Direct (more in the future) and that do not already
  // have a reaction
  return !!(
    item.isCustomerItem() &&
    item.content instanceof ConversationMessage &&
    supportedChannels.includes(item.content.channel) &&
    !item.content.hasReaction()
  );
}

function canUnreact(item, canSendOutboundMessages) {
  if (!canSendOutboundMessages) {
    return false;
  }

  const supportedChannels = [MessageChannelType.INSTAGRAM_DIRECT];
  // The reaction menu item should be present for Customer items of Conversation Item
  // type with a channel of Instagram Direct (more in the future) and that do not already
  // have a reaction
  return !!(
    item.isCustomerItem() &&
    item.content instanceof ConversationMessage &&
    supportedChannels.includes(item.content.channel) &&
    item.content.hasReaction()
  );
}

function canSendInstagramPublicReply(item) {
  return isUnmoderatedCustomerSocialComment(item);
}

function canSendFacebookCommentReply(context, item) {
  return (
    context.isFeatureEnabled('facebookCommentReplies') &&
    item.isCustomerItem() &&
    item.contentType() === ConversationItemType.CONVERSATION_MESSAGE &&
    item.content.channel === MessageChannelType.FB_COMMENT &&
    !item.content.getModerationStatus()
  );
}

function isUnmoderatedCustomerSocialComment(item) {
  return item.isCustomerItem() && isSocialComment(item) && !item.content.getModerationStatus();
}

function isSocialComment(item) {
  return (
    item.contentType() === ConversationItemType.CONVERSATION_MESSAGE &&
    item.content.channel === MessageChannelType.INSTAGRAM_COMMENT
  );
}
/**
 * Determines if the Instagram private reply option should be shown for a conversation item.
 *
 * Criteria:
 * - The item must be a customer Instagram comment without moderation status.
 * - The comment must be less than 7 days old.
 * - No agent-initiated private reply should exist for this comment.
 *
 * @param {Object} context - The context providing access to providers and features.
 * @param {Object} item - The conversation item to check.
 * @returns {boolean} True if the private reply option should be shown, false otherwise.
 */
function canSendInstagramPrivateReply(context, item) {
  if (!isUnmoderatedCustomerSocialComment(item)) {
    return false;
  }

  // Current time must be within 7 days of when the comment was made
  const sevenDaysAgo = moment().subtract(7, 'days');
  if (moment(item.timestamp).isSameOrBefore(sevenDaysAgo)) {
    return false;
  }

  // Check if a private reply has already been sent
  // Search for agent-initiated items created after this comment that have relatedItemId.conversationItemId equal to this item's ID
  const conversationHistory = context.getProvider('conversationHistory');
  const existingPrivateReply = conversationHistory.findBy({
    filter: historyItem => {
      if (historyItem.timestamp <= item.timestamp) {
        return false;
      }

      if (historyItem.initiator?.type !== 'AGENT') {
        return false;
      }

      if (historyItem.content?.channel !== MessageChannelType.INSTAGRAM_DIRECT) {
        return false;
      }

      if (historyItem.relatedItemId?.conversationItemId !== item.id) {
        return false;
      }

      const messageStatus = historyItem.content?.status?.value;
      return messageStatus === MessageStatus.SENT || messageStatus === MessageStatus.DELIVERED;
    },
  });

  // If a private reply already exists, don't show the menu option
  return !existingPrivateReply;
}

function mapExecuteToProps(executeAction, props) {
  return {
    onCopyLink: () => executeAction(CopyItemLink, { item: props.item }),
    onDeleteFacebookComment: () =>
      executeAction(
        OpenModal,
        new DeleteFacebookComment({
          commentId: props.item.content.externalId,
        })
      ),
    onDeleteRecording: () => {
      let recordingType = props.item.contentType();
      let conversationItemId = props.item.id;
      if (props.item.content instanceof VoiceAICall) {
        recordingType = ConversationItemType.VOICE_AI_RECORDING;
        conversationItemId = props.item.content.getVoiceAIRecording().id;
      }

      executeAction(ShowDeleteVoiceRecordingModal, {
        customerId: props.customerId,
        conversationItemId,
        recordingType,
      });
    },
    onEditTask: () => executeAction(EditTaskItem, { item: props.item }),
    onFollowTask: () => executeAction(FollowTask, { itemId: props.item.id }),
    onForward: () => executeAction(ForwardEmail, { item: props.item }),
    onHideFacebookComment: () =>
      executeAction(HideFacebookComment, {
        commentId: props.item.content.externalId,
      }),
    onHighlightGroupedMessages: () =>
      executeAction(HighlightGroupedMessages, {
        customerId: props.customerId,
        id: getMessageGroupId(props.item),
        isHighlighted: true,
      }),
    onInstagramPrivateReply: () =>
      executeAction(CreateInstagramPrivateReplyComposition, {
        conversationId: props.item.conversationId,
        conversationItem: props.item,
      }),
    onFacebookCommentReply: () =>
      executeAction(CreateFacebookCommentReplyComposition, {
        conversationId: props.item.conversationId,
        conversationItem: props.item,
      }),
    onInstagramPublicReply: () =>
      executeAction(CreateInstagramPublicReplyComposition, {
        conversationId: props.item.conversationId,
        conversationItem: props.item,
      }),
    onNoReplyNeeded: () => executeAction(AddNoReplyNeeded, {}),
    onObserveCall: () => {
      executeAction(ShowAddObserverToCallModal, {
        customerId: props.customerId,
        conversationItemId: props.item.id,
      });
    },
    onReaction: () => executeAction(AddReaction, { item: props.item }),
    onRedact: () =>
      executeAction(
        OpenModal,
        new RedactConversationItem({
          itemId: props.item.id,
          customerId: props.customerId,
        })
      ),
    onReply: () => executeAction(ReplyToEmail, { item: props.item }),
    onReplyToGroupMessage: () => executeAction(ReplyToGroupMessage, { item: props.item }),
    onReportPoorCallQuality: () => {
      executeAction(ReportPoorCallQuality, {
        customerId: props.customerId,
        conversationItem: props.item,
      });
    },
    onUnfollowTask: () => executeAction(UnfollowTask, { itemId: props.item.id }),
    onUnreaction: () => executeAction(RemoveReaction, { item: props.item }),
    onUnselectHighlightGroupedMessages: () =>
      executeAction(HighlightGroupedMessages, {
        customerId: props.customerId,
        id: getMessageGroupId(props.item),
        isHighlighted: false,
      }),
    onViewSidekickConversation: () => {
      executeAction(NavigateToSidekickConversation, {
        item: props.item,
      });
    },
    openSidePanel: () =>
      executeAction(OpenOrRefreshCommentPanel, {
        itemId: props.item.id,
        location: TaskAnalyticsLocations.CONVERSATION_ITEM_MENU,
      }),
  };
}

export const ConversationItemMenuContainer = connect(mapStateToProps, mapExecuteToProps)(ConversationItemMenuItems);
ConversationItemMenuContainer.propTypes = {
  customerId: PropTypes.string.isRequired,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  isNoReplyNeededVisible: PropTypes.bool,
  onToggleTranslatedText: PropTypes.func,
  isViewTranslation: PropTypes.bool,
};

function mapWrappedStateToProps({ getProvider }, props) {
  let currentAgent = getProvider('currentAgent').get();
  return {
    customerId: getProvider('currentLocation').get().customerId,
    currentAgentId: _.get(currentAgent, 'id'),
    isNoReplyNeededVisible: isNoReplyNeededVisible(),
    ...props,
  };

  function isNoReplyNeededVisible() {
    const { lastCustomerItemId, latestConversation, item } = props;
    if (item.id === lastCustomerItemId && isNRNSupported(item)) {
      return true;
    }

    if (hasSla(latestConversation)) {
      if (latestConversation?.sla?.setByConversationItemId === item.id || item.id === lastCustomerItemId) {
        return true;
      }
    }

    return false;
  }

  function isNRNSupported(conversationItem) {
    let unsupportedSessionTypes = [InteractionType.PHONE_CALL, InteractionType.TASK];
    return (
      unsupportedSessionTypes.indexOf(getInteractionType(conversationItem.contentType(), conversationItem.content)) ===
      -1
    );
  }

  function hasSla(conversation) {
    return !!conversation?.sla?.dueAt;
  }
}

const WrappedConversationItemMenuContainer = connect(mapWrappedStateToProps)(ConversationItemMenuContainer);

WrappedConversationItemMenuContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  lastCustomerItemId: PropTypes.string,
  latestConversation: PropTypes.object,
  onOpenPinToCustomer: PropTypes.func,
  onOpenTranslateMenu: PropTypes.func,
  onToggleTranslatedText: PropTypes.func,
  isViewTranslation: PropTypes.bool,
};

export default function ItemMenuContainer(props) {
  const { toggleViewTranslation, isViewTranslation, handleTranslate } = useContext(ItemContext);
  const { lastCustomerItem, latestConversation } = useContext(ItemsContext);

  return (
    <WrappedConversationItemMenuContainer
      isViewTranslation={isViewTranslation}
      lastCustomerItemId={lastCustomerItem?.id}
      latestConversation={latestConversation}
      onToggleTranslatedText={toggleViewTranslation}
      onTranslateInto={handleTranslate}
      {...props}
    />
  );
}
